import { useAuthStore } from '@stores/authStore';
import { useQueryClient } from '@tanstack/vue-query';
import { uploadQueries, uploadKeys } from '@therame/api-client';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useUserProfileImageStore = defineStore('userProfileImage', () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  const currentProfileImageUrl = ref<string | undefined>();

  const loadCurrentProfileImage = async () => {
    if (authStore.authContext?.user?.id) {
      loadProfileImageByUserId(authStore.authContext?.user?.id)
        .then((fileUrl) => {
          currentProfileImageUrl.value = fileUrl;
        })
        .catch(() => {
          currentProfileImageUrl.value = undefined;
        });
    }
  };

  const profileImageUrls = ref<Map<string, string>>(new Map());

  const loadProfileImageByUserId = async (userId: string) => {
    try {
      const blob = await queryClient.fetchQuery({
        queryKey: uploadKeys.profileImage(userId),
        queryFn: () => uploadQueries.getProfileImage(userId),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });

      if (!blob || blob.size === 0) {
        return undefined;
      }

      const file = new File([blob], userId);
      const url = URL.createObjectURL(file);
      profileImageUrls.value = new Map(profileImageUrls.value).set(userId, url);
      return url;
    } catch {
      return undefined;
    }
  };

  const invalidateProfileImage = async (userId: string) => {
    await queryClient.invalidateQueries({ queryKey: uploadKeys.profileImage(userId) });
  };

  return {
    loadProfileImageByUserId,
    loadCurrentProfileImage,
    invalidateProfileImage,
    currentProfileImageUrl,
    profileImageUrls,
  };
});
